/**
 * Which senders may load remote images in their mail, as system state.
 *
 * A remote image is a read receipt: fetching one tells the sender the message
 * was opened, when, and roughly from where. The renderer blocks them all, and
 * this list is the only thing that opens the door — one sender at a time,
 * granted on purpose in the reader and revocable in Settings.
 *
 * The list lives in an addressable event encrypted to this account alone, the
 * same shape the workflow map uses, for the same reason: nothing server-side
 * reads it, and the relay holds only ciphertext. It syncs, because a sender
 * granted on one browser is granted everywhere or the permission means nothing.
 */
import type { Event as NostrEvent } from "nostr-tools";
import { gzipSync, gunzipSync } from "fflate";
import type { RelayConnection } from "./relay";
import type { MailSigner } from "./signer";
import { KIND_APP_DATA, decodeBody, toBase64 } from "./contacts";

export const IDENTIFIER = "postr/image-senders";

export interface ImageSenderList {
  version: number;
  updated: string;
  /** Sender addresses, lowercased. An empty list blocks every sender. */
  senders: string[];
}

export interface ImageSenderRecord {
  list: ImageSenderList;
  /** When the event was written, so the next read can skip an unchanged list. */
  createdAt: number;
}

/**
 * Read the list, and only when it has changed.
 *
 * `newerThan` is the `createdAt` of the last read. A decrypt is a round-trip
 * to the phone for a remote signer, and the poller runs every minute, so
 * reading a list nobody has rewritten costs an approval for nothing.
 */
export async function fetchImageSenders(
  relay: RelayConnection,
  ownerPubkey: string,
  signer: MailSigner,
  newerThan = 0,
): Promise<ImageSenderRecord | null> {
  const events = await relay.list({
    kinds: [KIND_APP_DATA],
    authors: [ownerPubkey],
    "#d": [IDENTIFIER],
    limit: 1,
  });
  const newest = events.sort((a, b) => b.created_at - a.created_at)[0];
  if (!newest || newest.created_at <= newerThan) return null;

  const plaintext = await signer.nip44Decrypt(ownerPubkey, newest.content);
  const json = new TextDecoder().decode(gunzipSync(decodeBody(plaintext)));
  return { list: JSON.parse(json) as ImageSenderList, createdAt: newest.created_at };
}

/**
 * Publish a new version.
 *
 * An empty list is published, not withdrawn: revoking the last permission must
 * reach every other browser, and an absent event cannot say that.
 *
 * `after` is the `created_at` of the last list this client saw or sent. The
 * stamp never goes backwards, so two permissions granted inside one second
 * cannot tie — a relay resolving a tie picks by id, which is a coin toss
 * about which grant survives everywhere.
 */
export async function publishImageSenders(
  relay: RelayConnection,
  signer: MailSigner,
  senders: readonly string[],
  version: number,
  after = 0,
): Promise<NostrEvent> {
  const list: ImageSenderList = {
    version,
    updated: new Date().toISOString(),
    senders: [...new Set(senders.map((s) => s.trim().toLowerCase()).filter(Boolean))].sort(),
  };
  const squeezed = gzipSync(new TextEncoder().encode(JSON.stringify(list)));
  const content = await signer.nip44Encrypt(signer.pubkey, toBase64(squeezed));

  const event = await signer.signEvent({
    kind: KIND_APP_DATA,
    created_at: Math.max(Math.floor(Date.now() / 1000), after + 1),
    tags: [["d", IDENTIFIER]],
    content,
  });
  await relay.publish(event);
  return event;
}
